import { errors, User, UserProfile } from '~/prix'
import { VariableAndOptionalResult } from './variables.typing'
import {
  Layer,
  Space,
  SpaceChildrenItem,
  SpaceDelimitationGeoStandard,
  SpaceGeoStandardType,
  SpaceType,
  Time,
} from './spaceTime.types'
import {
  currentOnlyTimeTypes,
  generateIncidenceDates,
  incidenceTimeTypes,
  prevalenceTimeTypes,
  timeTypeForIncidence,
} from '~/prix/types/presets/time'
import { stringifyDate } from '~/prix/types/date'
import {
  childrenItemViewLabels,
  compatibleSpaceChildren,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from './spaceTime.data'

type VariableWithTimeType = Pick<VariableAndOptionalResult, 'timeType'>

export type PreferredSpace = {
  delimitationType: SpaceGeoStandardType
  delimitationId: number
  delimitationTitle: string
  childrenType?: SpaceGeoStandardType | 'item'
}

export function getDefaultTimeForVariable(variable: VariableWithTimeType): Time {
  if (variable.timeType === 'currentOnly') {
    return {
      type: 'current',
      title: 'Atual',
    }
  } else if (variable.timeType === 'prevalence') {
    throw errors.incompatible('Não implementado')
  } else if (variable.timeType === 'incidence') {
    const type = 'currentYearSubOneDay'
    const [startDate, endDate] = generateIncidenceDates[type]()
    const [newStartDate, newEndDate] = [stringifyDate(startDate), stringifyDate(endDate)]

    const timeLabels = timeTypeForIncidence.keysAndLabels || {}
    const title = timeLabels[type as keyof typeof timeLabels] || 'Ano atual até ontem'

    return {
      type,
      title,
      startDate: newStartDate,
      endDate: newEndDate,
    }
  } else {
    throw new Error('Invalid time type')
  }
}

export function currentTimeIfCompatible(
  variable: VariableWithTimeType,
  current?: Pick<Layer, 'time'> | Time | null,
): Time | null {
  if (!current) {
    return null
  }

  const time = 'time' in current ? current.time : current

  if (variable.timeType === 'incidence' && incidenceTimeTypes.includes(time.type)) {
    return time
  }
  if (variable.timeType === 'currentOnly' && currentOnlyTimeTypes.includes(time.type)) {
    return time
  }
  if (variable.timeType === 'prevalence' && prevalenceTimeTypes.includes(time.type)) {
    return time
  }
  return null
}

export function normalizeTimeForVariable(
  variable: VariableWithTimeType,
  current?: Pick<Layer, 'time'> | Time | null,
): Time {
  return currentTimeIfCompatible(variable, current) ?? getDefaultTimeForVariable(variable)
}

export function normalizeLayerTimeForVariable(layer: Layer): Layer {
  const normalizedTime = normalizeTimeForVariable(
    layer.variable as VariableWithTimeType,
    layer.time,
  )

  if (normalizedTime === layer.time) {
    return layer
  }

  return {
    ...layer,
    time: normalizedTime,
  }
}

function getCompatibleChildrenTypes(
  variable: VariableAndOptionalResult,
  delimitationType: Space['delimitationType'],
): Space['childrenType'][] {
  return variable.compatibleChildrenTypes
    ? compatibleSpaceChildren[delimitationType].filter(type =>
        variable.compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[delimitationType]
}

function getChildrenItemTitle(childrenItemView: SpaceChildrenItem['childrenItemView']): string {
  if (childrenItemView === 'item') {
    return spaceTypePluralLabels.item
  }

  return childrenItemViewLabels[childrenItemView] ?? childrenItemViewLabels.heatmap
}

function normalizeCompatibleSpace(variable: VariableAndOptionalResult, space: Space): Space | null {
  const allCompatibleChildrenTypes = getCompatibleChildrenTypes(variable, space.delimitationType)
  if (!allCompatibleChildrenTypes.length) {
    return null
  }

  const fallbackChildrenType = allCompatibleChildrenTypes.includes(space.delimitationType)
    ? space.delimitationType
    : allCompatibleChildrenTypes[0]
  const childrenType = allCompatibleChildrenTypes.includes(space.childrenType)
    ? space.childrenType
    : fallbackChildrenType

  if (space.delimitationType === 'custom') {
    if (childrenType !== 'item') {
      return null
    }

    const childrenItemView =
      space.childrenType === 'item' ? (space as SpaceChildrenItem).childrenItemView : 'heatmap'

    return {
      delimitationType: 'custom',
      delimitationTitle: space.delimitationTitle,
      delimitationCustom: space.delimitationCustom,
      childrenType: 'item',
      childrenTitle: getChildrenItemTitle(childrenItemView),
      childrenItemView,
      childrenH3Resolution:
        space.childrenType === 'item'
          ? (space as SpaceChildrenItem).childrenH3Resolution
          : undefined,
      colorMap: space.colorMap,
    }
  }

  const baseSpace = {
    delimitationTitle: space.delimitationTitle,
    delimitationType: space.delimitationType,
    delimitationId: (space as SpaceDelimitationGeoStandard).delimitationId,
    colorMap: space.colorMap,
  }

  if (childrenType === 'item') {
    const childrenItemView =
      space.childrenType === 'item' ? (space as SpaceChildrenItem).childrenItemView : 'heatmap'

    return {
      ...baseSpace,
      childrenType: 'item',
      childrenTitle: getChildrenItemTitle(childrenItemView),
      childrenItemView,
      childrenH3Resolution:
        space.childrenType === 'item'
          ? (space as SpaceChildrenItem).childrenH3Resolution
          : undefined,
    } as Space
  }

  return {
    ...baseSpace,
    childrenType,
    childrenTitle:
      childrenType === space.delimitationType
        ? spaceTypeSingularLabels[childrenType]
        : spaceTypePluralLabels[childrenType],
  } as Space
}

function getDefaultSpaceForUser(variable: VariableAndOptionalResult, user: UserProfile): Space {
  const delimitationType = user.activeClient.settings.general.preferredSpaceDelimitationType
  const allCompatibleChildrenTypes = variable.compatibleChildrenTypes
    ? compatibleSpaceChildren[delimitationType as SpaceType].filter(type =>
        variable.compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[delimitationType as SpaceType]
  const childrenType = allCompatibleChildrenTypes.includes(
    user.activeClient.settings.general.preferredSpaceChildrenType,
  )
    ? user.activeClient.settings.general.preferredSpaceChildrenType
    : allCompatibleChildrenTypes[0]
  const space: Space = {
    delimitationTitle: user.activeClient.settings.general.preferredSpaceDelimitationTitle,
    delimitationType,
    delimitationId: user.activeClient.settings.general.preferredSpaceDelimitationId,
    childrenType,
    childrenItemView: childrenType === 'item' ? 'heatmap' : undefined,
  } as Space
  return space
}

function getDefaultSpaceForOptions({
  variable,
  hasPermissions,
  stateId,
  stateName,
  preferredSpace,
}: {
  variable: VariableAndOptionalResult
  hasPermissions: boolean | undefined
  stateId: number | null
  stateName: string | null
  preferredSpace?: PreferredSpace | null
}): Space {
  if (hasPermissions) {
    return normalizeCompatibleSpace(variable, {
      childrenType: 'state',
      childrenTitle: spaceTypePluralLabels.state,
      delimitationId: 30,
      delimitationTitle: 'Brasil',
      delimitationType: 'country',
    } as Space) as Space
  }

  if (!preferredSpace && (typeof stateId !== 'number' || Number.isNaN(stateId) || !stateName)) {
    const allCompatibleChildrenTypes = getCompatibleChildrenTypes(variable, 'country')

    return normalizeCompatibleSpace(variable, {
      childrenType: allCompatibleChildrenTypes[0] || 'state',
      childrenTitle: spaceTypePluralLabels[allCompatibleChildrenTypes[0] || 'state'],
      delimitationId: 30,
      delimitationTitle: 'Brasil',
      delimitationType: 'country',
    } as Space) as Space
  }

  const delimitationType = preferredSpace?.delimitationType ?? 'state'
  const allCompatibleChildrenTypes = getCompatibleChildrenTypes(variable, delimitationType)
  const childrenType =
    preferredSpace?.childrenType && allCompatibleChildrenTypes.includes(preferredSpace.childrenType)
      ? preferredSpace.childrenType
      : allCompatibleChildrenTypes[0]

  return normalizeCompatibleSpace(variable, {
    delimitationTitle: preferredSpace?.delimitationTitle ?? stateName ?? undefined,
    delimitationType,
    delimitationId: preferredSpace?.delimitationId ?? stateId ?? 0,
    childrenType,
    childrenTitle: childrenType === delimitationType ? delimitationType : childrenType,
  } as Space) as Space
}

function currentSpaceIfCompatible(
  variable: VariableAndOptionalResult,
  current?: Layer,
): Space | null {
  if (!current) {
    return null
  }

  return normalizeCompatibleSpace(variable, current.space)
}

export function normalizeLayerSpaceForVariable(
  layer: Layer,
  options: {
    hasPermissions: boolean | undefined
    stateId: number | null
    stateName: string | null
    preferredSpace?: PreferredSpace | null
  },
): Layer {
  if ('path' in layer.variable) {
    return layer
  }

  const currentSpace = currentSpaceIfCompatible(layer.variable, layer)
  const space =
    currentSpace ??
    getDefaultSpaceForOptions({
      variable: layer.variable,
      hasPermissions: options.hasPermissions,
      stateId: options.stateId,
      stateName: options.stateName,
      preferredSpace: options.preferredSpace,
    })

  return {
    ...layer,
    space,
  }
}

export default function recommendedLayerForVariable(
  variable: VariableAndOptionalResult,
  user: UserProfile,
  current?: Layer,
): Layer
export default function recommendedLayerForVariable(
  variable: VariableAndOptionalResult,
  hasPermissions: boolean | undefined,
  stateId: number | null,
  stateName: string | null,
  current?: Layer,
  preferredSpace?: PreferredSpace | null,
): Layer
export default function recommendedLayerForVariable(
  variable: VariableAndOptionalResult,
  userOrHasPermissions: UserProfile | boolean | undefined,
  stateIdOrCurrent?: number | Layer | null,
  stateName?: string | null,
  current?: Layer,
  preferredSpace?: PreferredSpace | null,
): Layer {
  const time: Time =
    currentTimeIfCompatible(
      variable,
      (typeof userOrHasPermissions === 'object' &&
      userOrHasPermissions &&
      'activeClient' in userOrHasPermissions
        ? (stateIdOrCurrent as Layer | null | undefined)
        : current) ?? undefined,
    ) ?? getDefaultTimeForVariable(variable)

  const usingUserProfile =
    typeof userOrHasPermissions === 'object' &&
    userOrHasPermissions !== null &&
    'activeClient' in userOrHasPermissions

  const currentLayer = usingUserProfile
    ? ((stateIdOrCurrent as Layer | null | undefined) ?? undefined)
    : current

  const space: Space = usingUserProfile
    ? (currentSpaceIfCompatible(variable, currentLayer) ??
      getDefaultSpaceForUser(variable, userOrHasPermissions))
    : normalizeLayerSpaceForVariable(
        {
          variable,
          time,
          space:
            currentLayer?.space ??
            getDefaultSpaceForOptions({
              variable,
              hasPermissions:
                typeof userOrHasPermissions === 'boolean' ? userOrHasPermissions : undefined,
              stateId: typeof stateIdOrCurrent === 'number' ? stateIdOrCurrent : null,
              stateName: stateName ?? null,
              preferredSpace,
            }),
        },
        {
          hasPermissions:
            typeof userOrHasPermissions === 'boolean' ? userOrHasPermissions : undefined,
          stateId: typeof stateIdOrCurrent === 'number' ? stateIdOrCurrent : null,
          stateName: stateName ?? null,
          preferredSpace,
        },
      ).space

  const analyzeResults = variable.result?.analyzeResults

  return {
    variable,
    space,
    time,
    analyzeResults,
  }
}
